import EditOutlinedIcon from "@mui/icons-material/EditOutlined";
import { Button, Divider, Stack } from "@mui/material";
import { useEffect } from "react";
import { useSelector } from "react-redux";
import { useNavigate } from "react-router";
import { ReportAccessType } from "@entrilia/shared/api/reportAccessTypes.ts";
import ReportAuthorizedBox from "@entrilia/shared/components/ReportAuthorizedBox";
import { getEditApiReportUrl } from "../../../../report-lists/reports/utilities/editReportUrl.ts";
import { useAppDispatch } from "../../../store/hooks/useAppDispatch";
import {
  selectCurrentReport,
  selectCurrentReportAuthorizationInfo,
} from "../../../store/selectors/currentReportSelectors";
import { createCurrentReportAction } from "../../../store/utils/storeUtils";
import ReportSelector from "../../common/report-selector/ReportSelector";
import ReportProductName from "../../common/ReportProductName.tsx";
import type { RunState } from "../../viewer/storage/runState.ts";
import { isTabularRunState } from "../../viewer/storage/runStateUtils.ts";
import { ExportStateContextProvider } from "../common/contexts/ExportStateContextProvider";
import ExportAction from "../common/saving/ExportAction";
import MoreAction from "../common/saving/MoreAction";
import ShareReportButton from "../common/sharing/ShareReportButton";
import ReportActionHeader from "../ReportActionHeader";
import TabularContainer from "../tabular/preview/TabularContainer";
import ReportViewer from "./viewer/ReportViewer";

interface Props {
  runState: RunState;
}
const ViewReportPage = ({ runState }: Props) => {
  const navigate = useNavigate();
  const dispatch = useAppDispatch();
  const report = useSelector(selectCurrentReport);
  const authorizationInfo = useSelector(selectCurrentReportAuthorizationInfo);

  useEffect(() => {
    dispatch(createCurrentReportAction("updateReportMode", false));
  }, [dispatch]);

  if (report === undefined) return <></>;

  return (
    <ExportStateContextProvider>
      <Stack sx={{ height: "100%" }}>
        <ReportActionHeader
          ProductName={<ReportProductName title="API Reporting" />}
          ReportSelector={<ReportSelector requiredAccess={ReportAccessType.Read} groups={undefined} />}
        >
          <Stack direction={"row"} sx={{ gap: 1, height: 32 }}>
            <ShareReportButton />
            <ExportAction />
            <ReportAuthorizedBox
              permissions={["ManageReports"]}
              access={authorizationInfo?.access}
              requiredAccess={ReportAccessType.Write}
            >
              <Button
                variant="outlined"
                color="secondary"
                sx={(theme) => ({ svg: { color: theme.palette.secondary.light } })}
                startIcon={<EditOutlinedIcon />}
                onClick={() => navigate(getEditApiReportUrl(report.clientCode, report.reportId))}
              >
                Edit Report
              </Button>
              <MoreAction report={report} />
            </ReportAuthorizedBox>
          </Stack>
        </ReportActionHeader>
        <Divider />
        {isTabularRunState(runState) && (
          <TabularContainer>
            <ReportViewer runState={runState} />
          </TabularContainer>
        )}
      </Stack>
    </ExportStateContextProvider>
  );
};

export default ViewReportPage;
